import { Link } from "react-router-dom";
import { ArrowUpRight, PhoneCall, FileText, SearchCheck, BadgeCheck } from "lucide-react";
import Seo from "@/components/Seo";
import ClaimIntakeForm from "@/components/ClaimIntakeForm";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const STEP_ICONS = [PhoneCall, FileText, SearchCheck, BadgeCheck];

export default function ClaimsSupport() {
  const { t } = useLanguage();
  return (
    <div data-testid="claims-support-page">
      <Seo
        title="Claims Support | Wisdom Insurance Broking Pune"
        description="Hands-on insurance claim assistance: intimation, documentation, surveyor coordination and settlement follow-up with insurers, from an IRDAI-licensed broker in Pune."
        keywords="insurance claim assistance Pune, claim settlement support, insurance broker claims help India"
      />

      <section className="grain bg-navy-deep pb-16 pt-32 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn>
            <Eyebrow light>{t("cl.eyebrow")}</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              {t("cl.title")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {t("cl.sub")}
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 md:py-24 lg:px-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {STEP_ICONS.map((Icon, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div data-testid={`claims-step-0${i + 1}`} className="h-full border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50 hover:shadow-lg lg:p-10">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-sm text-amber-700">{`0${i + 1}`}</span>
                  <Icon className="h-7 w-7 text-slate-400" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-heading text-lg font-semibold text-navy sm:text-xl">{t(`cl.s${i + 1}t`)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(`cl.s${i + 1}x`)}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn className="mt-14 border-l-4 border-amber-600 bg-slate-50 p-8">
          <h3 className="font-heading text-lg font-semibold text-navy">{t("cl.alertTitle")}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {t("cl.alertText")}
          </p>
          <a
            href="#claim-intake"
            data-testid="claims-contact-cta"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("claim-intake")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group mt-5 inline-flex items-center gap-2 bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
          >
            {t("cl.alertCta")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </FadeIn>
      </section>

      {/* EMERGENCY CLAIM INTIMATION */}
      <section id="claim-intake" data-testid="claim-intake-section" className="scroll-mt-24 border-t border-slate-200 bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn>
            <Eyebrow>{t("cf.eyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              {t("cf.title")}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              {t("cf.sub")}
            </p>
          </FadeIn>
          <FadeIn delay={0.15} className="mt-10 border border-slate-200 bg-white p-6 sm:p-10">
            <ClaimIntakeForm />
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
